import React from 'react';
import {
  Card, Row, Col, Input, Button, Space, Pagination,
  Select, Tooltip, Popconfirm, Badge, Divider
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined,
  PlayCircleOutlined, FilterOutlined
} from '@ant-design/icons';

const { Option } = Select;

const ReusableTable = ({
  // Props principales
  title,
  data = [],
  columns = [],
  loading = false,
 
  // Pagination
  totalItems = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange = () => {},
  onSizeChange = () => {},
 
  // Recherche et filtres
  searchText = '',
  onSearchChange = () => {},
  onSearch = () => {},
  filters = [],
  onFilterChange = () => {},
  showSearch = true,
  showFilters = true,
 
  // Actions
  onAdd = null,
  onRefresh = null,
  onEdit = null,
  onDelete = null,
  onView = null,
  onStatusChange = null,
  onCustomAction = null,
 
  // Personnalisation
  addButtonText = 'Ajouter',
  searchPlaceholder = 'Search for...',
  showEntriesInfo = true,
  showStats = false,
  stats = {},
 
  // Configuration des colonnes
  showIdColumn = true,
  showStatusColumn = false,
  showActionColumn = true,
 
  // Configuration des actions
  actionButtons = ['view', 'edit', 'delete'],
  customActions = [],
 
  // Options de statut
  statusOptions = [],
 
  // Personnalisation
  primaryColor = '#5fa5e7',
  borderColor = '#434343',
 
  // Options supplémentaires
  extraFilters = null,
  extraButtons = null,
  tableTitle = null,
 
  // Contenu personnalisé
  headerContent = null,
  footerContent = null,
 
  // Classes CSS
  containerClassName = '',
  cardClassName = ''
}) => {
  // Format ID pour l'affichage
  const formatId = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  // Colonnes par défaut
  const defaultColumns = [
    // Colonne ID
    ...(showIdColumn ? [{
      title: 'ID',
      key: 'id',
      width: 80,
      render: (_, record, index) => formatId(index),
      cellStyle: {
        color: '#495057',
        textAlign: 'center'
      }
    }] : []),
   
    // Colonnes personnalisées
    ...columns.map(col => ({
      ...col,
      cellStyle: {
        color: '#495057',
        fontWeight: col.bold ? '600' : 'normal',
        ...col.cellStyle
      }
    })),
   
    // Colonne de statut
    ...(showStatusColumn ? [{
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        const statusOption = statusOptions.find(s => s.value === status);
        if (statusOption) {
          return (
            <span style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: statusOption.backgroundColor || '#f6ffed',
              color: statusOption.color || '#52c41a',
              border: `1px solid ${statusOption.borderColor || '#b7eb8f'}`,
              minWidth: '100px',
              textAlign: 'center'
            }}>
              {statusOption.icon && <span style={{ marginRight: '4px' }}>{statusOption.icon}</span>}
              {status}
            </span>
          );
        }
        return status;
      },
      cellStyle: { textAlign: 'center' }
    }] : []),
   
    // Colonne d'actions
    ...(showActionColumn ? [{
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'center' }}>
          {actionButtons.includes('view') && onView && (
            <Tooltip title="Voir détails">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => onView(record)}
                style={{
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  fontSize: '12px',
                  padding: '4px',
                  height: '32px',
                  width: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Tooltip>
          )}
         
          {actionButtons.includes('edit') && onEdit && (
            <Tooltip title="Modifier">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => onEdit(record)}
                style={{
                  backgroundColor: '#049472',
                  borderColor: '#036951',
                  fontSize: '12px',
                  padding: '4px',
                  height: '32px',
                  width: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Tooltip>
          )}
         
          {actionButtons.includes('status') && onStatusChange && (
            <Tooltip title="Changer statut">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                size="small"
                onClick={() => onStatusChange(record)}
                style={{
                  backgroundColor: '#fa8c16',
                  borderColor: '#d46b08',
                  fontSize: '12px',
                  padding: '4px',
                  height: '32px',
                  width: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Tooltip>
          )}
         
          {customActions.map((action, index) => (
            <Tooltip key={index} title={action.tooltip}>
              <Button
                type={action.type || 'primary'}
                icon={action.icon}
                size="small"
                onClick={() => action.onClick(record)}
                style={{
                  backgroundColor: action.color || '#722ed1',
                  borderColor: action.borderColor || '#722ed1',
                  fontSize: '12px',
                  padding: '4px',
                  height: '32px',
                  width: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Tooltip>
          ))}
         
          {actionButtons.includes('delete') && onDelete && (
            <Tooltip title="Supprimer">
              <Popconfirm
                title="Êtes-vous sûr de supprimer ?"
                onConfirm={() => onDelete(record)}
                okText="Oui"
                cancelText="Non"
                okButtonProps={{
                  style: {
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545'
                  }
                }}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    color: 'white',
                    fontSize: '12px',
                    padding: '4px',
                    height: '32px',
                    width: '32px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
      cellStyle: {
        textAlign: 'center',
        padding: '8px'
      }
    }] : [])
  ];

  return (
    <div className={containerClassName} style={{
      padding: '4px 20px',
      backgroundColor: '#dedede',
      minHeight: '50vh',
      borderRadius: '12px',
      boxShadow: '0 12px 12px rgba(66, 65, 65, 0.1)',
    }}>
      <Card className={cardClassName} style={{
        borderRadius: '2px',
        border: `2px solid ${borderColor}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {/* Header avec contenu personnalisé */}
        {headerContent || (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{ flex: 1 }}>
                <h1 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {title}
                </h1>
               
                {/* Statistiques */}
                {showStats && stats && (
                  <div style={{
                    fontSize: '14px',
                    color: '#666',
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    {Object.entries(stats).map(([key, value]) => (
                      <Badge
                        key={key}
                        count={value}
                        style={{
                          backgroundColor: key === 'total' ? primaryColor :
                                         key === 'actif' ? '#52c41a' :
                                         key === 'inactif' ? '#f5222d' : '#fa8c16'
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
             
              {/* Bouton d'ajout */}
              {onAdd && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAdd}
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    padding: '8px 20px',
                    height: 'auto',
                    fontWeight: '500',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {addButtonText}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Entries info */}
        {showEntriesInfo && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#666',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Show {pageSize} entries</span>
            {totalItems > 0 && (
              <span>Total: <strong>{totalItems}</strong> items</span>
            )}
          </div>
        )}

        {/* Search and filters bar */}
        <div style={{ marginBottom: '20px' }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Recherche */}
            {showSearch && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Input
                  placeholder={searchPlaceholder}
                  value={searchText}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onPressEnter={onSearch}
                  prefix={<SearchOutlined />}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Col>
            )}
           
            {/* Filtres */}
            {showFilters && filters.map((filter, index) => (
              <Col key={index} xs={24} sm={12} md={8} lg={6}>
                <Select
                  placeholder={filter.placeholder}
                  value={filter.value}
                  onChange={(value) => onFilterChange(filter.field, value)}
                  allowClear
                  style={{ width: '100%' }}
                  suffixIcon={<FilterOutlined />}
                >
                  {filter.options.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Col>
            ))}
           
            {/* Filtres supplémentaires */}
            {extraFilters && (
              <Col xs={24} sm={12} md={8} lg={6}>
                {extraFilters}
              </Col>
            )}
           
            {/* Boutons d'action */}
            <Col xs={24} sm={24} md={24} lg={24} style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              {/* Bouton de recherche */}
              {showSearch && (
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={onSearch}
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    fontWeight: '500'
                  }}
                >
                  Rechercher
                </Button>
              )}
             
              {/* Bouton d'actualisation */}
              {onRefresh && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={onRefresh}
                  loading={loading}
                  style={{
                    borderColor: '#d9d9d9',
                    fontWeight: '500'
                  }}
                >
                  Actualiser
                </Button>
              )}
             
              {/* Boutons supplémentaires */}
              {extraButtons}
            </Col>
          </Row>
        </div>

        {/* Table Card */}
        <Card
          bordered={true}
          style={{
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            marginBottom: '20px',
            padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Titre du tableau */}
          {tableTitle && (
            <div style={{
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
              fontWeight: '600',
              fontSize: '16px',
              color: '#495057',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{tableTitle}</span>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {data.length} éléments
              </span>
            </div>
          )}
         
          {/* Table */}
          <div style={{ overflowX: 'auto', minHeight: '200px' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              tableLayout: 'fixed'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f3f3f3',
                  borderBottom: '2px solid #dee2e6'
                }}>
                  {defaultColumns.map((column, index) => (
                    <th
                      key={column.key || index}
                      style={{
                        padding: '16px 12px',
                        textAlign: column.cellStyle?.textAlign || 'center',
                        fontWeight: '600',
                        color: '#495057',
                        borderRight: index < defaultColumns.length - 1 ? '1px solid #dee2e6' : 'none',
                        width: column.width || 'auto',
                        minWidth: column.minWidth || 'auto',
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        top: 0,
                        backgroundColor: '#f8f9fa',
                        zIndex: 1
                      }}
                    >
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, rowIndex) => (
                  <tr
                    key={item.id || item._id || rowIndex}
                    style={{
                      borderBottom: '1px solid #e8e8e8',
                      backgroundColor: rowIndex % 2 === 0 ? 'white' : '#fafafa',
                      transition: 'background-color 0.3s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f7ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = rowIndex % 2 === 0 ? 'white' : '#fafafa';
                    }}
                  >
                    {defaultColumns.map((column, colIndex) => {
                      const value = item[column.dataIndex];
                     
                      // Render personnalisé
                      if (column.render) {
                        return (
                          <td
                            key={column.key || colIndex}
                            style={{
                              padding: '16px 12px',
                              borderRight: colIndex < defaultColumns.length - 1 ? '1px solid #e8e8e8' : 'none',
                              ...column.cellStyle
                            }}
                          >
                            {column.render(value, item, rowIndex)}
                          </td>
                        );
                      }
                     
                      // Render par défaut
                      return (
                        <td
                          key={column.key || colIndex}
                          style={{
                            padding: '16px 12px',
                            borderRight: colIndex < defaultColumns.length - 1 ? '1px solid #e8e8e8' : 'none',
                            ...column.cellStyle
                          }}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
               
                {/* Aucune donnée */}
                {data.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={defaultColumns.length}
                      style={{
                        padding: '60px 20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '16px',
                        fontStyle: 'italic'
                      }}
                    >
                      <div style={{ marginBottom: '16px' }}>
                        📭
                      </div>
                      Aucune donnée disponible
                    </td>
                  </tr>
                )}
               
                {/* Chargement */}
                {loading && (
                  <tr>
                    <td
                      colSpan={defaultColumns.length}
                      style={{
                        padding: '40px 20px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '16px'
                      }}
                    >
                      Chargement...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Footer avec pagination */}
        {footerContent || (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Divider */}
            <Divider style={{ margin: '8px 0' }} />
           
            {/* Pagination */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {/* Info de pagination */}
              <div style={{
                fontSize: '14px',
                color: '#666',
                textAlign: 'center',
                padding: '8px',
                backgroundColor: '#fafafa',
                borderRadius: '4px',
                border: '1px solid #e8e8e8'
              }}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </div>
             
              {/* Contrôles de pagination */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                {/* Sélecteur de page size */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Items per page:</span>
                  <Select
                    value={pageSize}
                    onChange={(value) => onSizeChange && onSizeChange(value)}
                    size="small"
                    style={{ width: '80px' }}
                  >
                    <Option value={5}>5</Option>
                    <Option value={10}>10</Option>
                    <Option value={20}>20</Option>
                    <Option value={50}>50</Option>
                  </Select>
                </div>
               
                {/* Pagination */}
                <div>
                  <Space>
                    <Button
                      disabled={currentPage === 1}
                      onClick={() => onPageChange(currentPage - 1, pageSize)}
                      style={{
                        color: currentPage === 1 ? '#ccc' : primaryColor,
                        borderColor: currentPage === 1 ? '#d9d9d9' : primaryColor
                      }}
                    >
                      Previous
                    </Button>
                   
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={totalItems}
                      onChange={onPageChange}
                      showSizeChanger={false}
                      size="small"
                      showLessItems
                      itemRender={(page, type, originalElement) => {
                        if (type === 'page') {
                          return (
                            <Button
                              type={page === currentPage ? 'primary' : 'default'}
                              size="small"
                              style={{
                                minWidth: '32px',
                                backgroundColor: page === currentPage ? primaryColor : 'transparent',
                                borderColor: page === currentPage ? primaryColor : '#d9d9d9',
                                color: page === currentPage ? 'white' : 'inherit'
                              }}
                            >
                              {page}
                            </Button>
                          );
                        }
                        return originalElement;
                      }}
                    />
                   
                    <Button
                      disabled={currentPage * pageSize >= totalItems}
                      onClick={() => onPageChange(currentPage + 1, pageSize)}
                      style={{
                        color: currentPage * pageSize >= totalItems ? '#ccc' : primaryColor,
                        borderColor: currentPage * pageSize >= totalItems ? '#d9d9d9' : primaryColor
                      }}
                    >
                      Next
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReusableTable;